/**
 * 地区码转换工具类
 * @description 支持获取省/省市/省市区，支持自定义分隔符
 */
export class AreaParser {
	/**
	 * @param {Array} areaList - 地区数据列表
	 * @param {Object} options - 配置项
	 * @param {string} options.separator - 分隔符，默认'-'
	 * @param {boolean} options.useCache - 是否使用缓存，默认true
	 */
	constructor(areaList = [], options = {}) {
		this.areaList = areaList
		this.separator = options.separator || '-'
		this.useCache = options.useCache ?? true
		this.cache = new Map()
	}

	/**
	 * 获取地区名称
	 * @param {string} areaCode - 地区编码
	 * @param {number} [level] - 地区层级(1:省, 2:省市, 3:省市区)，默认返回完整层级
	 * @returns {string} 地区名称
	 */
	getAreaName(areaCode, level = 3) {
		if (!areaCode || !this.areaList.length) return ''

		// 使用缓存
		const cacheKey = `${areaCode}_${level}`
		if (this.useCache && this.cache.has(cacheKey)) {
			return this.cache.get(cacheKey)
		}

		try {
			const names = []
			let currentLevel = 0

			// 处理省级(2位)
			const provinceCode = areaCode.substring(0, 2)
			const province = this.areaList.find((item) => item.areaCode.startsWith(provinceCode))

			if (!province) return ''
			names.push(province.areaName)
			currentLevel++

			// 如果只需要省级或地区码只有2位，直接返回
			if (level === 1 || areaCode.length === 2) {
				const result = names.join(this.separator)
				this.cacheResult(cacheKey, result)
				return result
			}

			// 处理市级(4位)
			const cityCode = areaCode.substring(0, 4)
			const city = province.children?.find((item) => item.areaCode.startsWith(cityCode))

			if (!city) {
				const result = names.join(this.separator)
				this.cacheResult(cacheKey, result)
				return result
			}

			names.push(city.areaName)
			currentLevel++

			// 如果只需要省市级或地区码只有4位，直接返回
			if (level === 2 || areaCode.length === 4) {
				const result = names.join(this.separator)
				this.cacheResult(cacheKey, result)
				return result
			}

			// 处理区级(6位)
			const district = city.children?.find((item) => item.areaCode === areaCode)

			if (district) {
				names.push(district.areaName)
				currentLevel++
			}

			const result = names.join(this.separator)
			this.cacheResult(cacheKey, result)
			return result
		} catch (error) {
			console.error('地区解析错误:', error)
			return ''
		}
	}

	/**
	 * 缓存结果
	 * @private
	 */
	cacheResult(key, value) {
		if (this.useCache) {
			this.cache.set(key, value)
		}
	}

	/**
	 * 清除缓存
	 */
	clearCache() {
		this.cache.clear()
	}

	/**
	 * 获取指定层级的地区码
	 * @param {string} areaCode - 完整地区码
	 * @param {number} level - 需要的层级(1:省, 2:市, 3:区)
	 * @returns {string} 对应层级的地区码
	 */
	getLevelCode(areaCode, level = 3) {
		if (!areaCode) return ''
		switch (level) {
			case 1:
				return areaCode.substring(0, 2) + '0000'
			case 2:
				return areaCode.substring(0, 4) + '00'
			default:
				return areaCode
		}
	}

	/**
	 * 解析地区完整信息
	 * @param {string} areaCode - 地区编码
	 * @returns {Object} 地区信息对象
	 */
	parseArea(areaCode) {
		return {
			province: this.getAreaName(areaCode, 1),
			city: this.getAreaName(areaCode, 2),
			full: this.getAreaName(areaCode, 3),
			provinceCode: this.getLevelCode(areaCode, 1),
			cityCode: this.getLevelCode(areaCode, 2),
			areaCode: this.getLevelCode(areaCode, 3),
		}
	}
}

// 创建默认实例
let defaultParser = null

/**
 * 获取地区解析器实例
 * @param {Array} areaList - 地区数据列表
 * @param {Object} options - 配置项
 * @returns {AreaParser} 地区解析器实例
 */
export const getAreaParser = (areaList, options = {}) => {
	if (!defaultParser) {
		defaultParser = new AreaParser(areaList, options)
	}
	return defaultParser
}

/**
 * 快速获取地区名称
 * @param {string} areaCode - 地区编码
 * @param {Array} areaList - 地区数据列表
 * @param {number} level - 地区层级
 * @returns {string} 地区名称
 */
export const getAreaName = (areaCode, areaList, level) => {
	const parser = getAreaParser(areaList)
	return parser.getAreaName(areaCode, level)
}
